// CSV building + download, for tables the browser already holds in full.
//
// Two kinds of table export in this app:
//   - Paged / infinite-scroll tables (Records, Sync Queue, every Rules
//     explorer) go through ``api/export.js``, because the browser has a page
//     and the CSV has to contain the whole filtered set.
//   - Tables whose endpoint returns everything in one shot (Dashboard,
//     Extraction batches, Mirror Locations) build the file here. "All rows"
//     and "the rows on screen" are the same set, so a round trip would buy
//     nothing.
//
// The rendering rules match ``backend/services/csv_export.py`` deliberately:
// same booleans, same list joining, same formula guard, same BOM. A user
// should not be able to tell which path produced their file.

// A cell starting with one of these executes as a formula when the CSV is
// opened in Excel or Sheets. '-' is NOT here — negative numbers are everyday
// values in billing data and must not be mangled.
const FORMULA_LEAD = ['=', '+', '@', '\t', '\r'];

export function csvCell(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.filter((v) => v != null).map(csvCell).join('; ');
  if (value instanceof Date) return value.toISOString().slice(0, 19).replace('T', ' ');
  if (typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => `${k}=${csvCell(v)}`).join('; ');
  }
  const text = String(value);
  return FORMULA_LEAD.includes(text[0]) ? `'${text}` : text;
}

function quote(text) {
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// ``columns``: [{ key, label, value? }] — ``value(row)`` for a cell the row
// doesn't carry verbatim (a computed total, a formatted duration).
export function toCsv(rows, columns) {
  const lines = [columns.map((c) => quote(csvCell(c.label))).join(',')];
  for (const row of rows) {
    lines.push(
      columns
        .map((c) => quote(csvCell(c.value ? c.value(row) : row[c.key])))
        .join(','),
    );
  }
  // \r\n is what every spreadsheet expects from a .csv, on every platform.
  return `${lines.join('\r\n')}\r\n`;
}

export function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Let the download start before the object URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function saveCsv(filename, text) {
  // Leading BOM: without it Excel on Windows reads the file as ANSI and
  // mangles every accented location name.
  saveBlob(filename, new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8' }));
}

// "locations" -> "locations-20260731-0605.csv". The stamp is what stops a
// week of exports from all being locations(3).csv in the Downloads folder.
export function stampedName(stem) {
  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`
    + `-${p(now.getHours())}${p(now.getMinutes())}`;
  return `${stem}-${stamp}.csv`;
}

// One call for the fully-loaded case: build the file and hand it to the user.
export function downloadCsv(stem, rows, columns) {
  saveCsv(stampedName(stem), toCsv(rows, columns));
}
